import { useState, useEffect } from 'react';
import Postmate from 'postmate';
import { getParamFromUrl } from '../utils/tools'
import {IRequisitionMatched} from '../types/dataMatched'


// 全局状态管理
let globalMessenger: Postmate.ChildAPI | null = null;
let globalIsReady: boolean = false;
let globalIsInitializing: boolean = false;
let initPromise: Promise<Postmate.ChildAPI> | null = null;

interface GlobalConfig {
  isActiveInfo?: any;
  sessionId?: string;
  specifications?: {id:string, color:string, name:string, icon:string}[];
  feeTypesData?: any;
  formComponents?: any;
  expenseLinkList?: IRequisitionMatched
  invoicePowers?: string[];
}

// 全局配置数据
let globalConfig: GlobalConfig = {};

// 关联申请选择的全局数据与监听（支持 toolCallId 过滤）
let globalExpenseLinks: { toolCallId?: string; selectedData: any[] } = { selectedData: [] };
const expenseLinkListeners = new Set<(payload: { toolCallId?: string; selectedData: any[] }) => void>();
const notifyExpenseLinkListeners = (payload: { toolCallId?: string; selectedData: any[] }) => {
  expenseLinkListeners.forEach(listener => listener(payload));
};

// 单据模板选择的全局数据与监听
let globalFlowTemplate: any = null;
const flowTemplateListeners = new Set<(template: any) => void>();
const notifyFlowTemplateListeners = (template: any) => {
  flowTemplateListeners.forEach(listener => listener(template));
};

// 费用类型选择的全局数据与监听
let globalFeeType: any = null;
const feeTypeListeners = new Set<(feeType: any) => void>();
const notifyFeeTypeListeners = (feeType: any) => {
  feeTypeListeners.forEach(listener => listener(feeType));
};

// 状态变更监听器
const listeners = new Set<() => void>();
const configListeners = new Set<() => void>();
const reimburseListeners = new Set<(payload: any[]) => void>();

// 通知所有监听器状态变更
const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

// 通知配置监听器
const notifyConfigListeners = () => {
  configListeners.forEach(listener => listener());
};

// 通知父应用触发的报销监听
const notifyReimburseListeners = (payload: any[]) => {
  reimburseListeners.forEach(listener => listener(payload));
};

let globalReimbursePayload: any[] = [];

/**
 * 检测是否在 iframe 中的 Hook
 */
export const useIsEmbedded = (): boolean => {
  return window.self !== window.top;
};

/**
 * 获取全局 messenger 实例
 */
export const getMessenger = (): Postmate.ChildAPI | null => {
  return globalMessenger;
};

/**
 * 获取全局就绪状态
 */
export const getIsReady = (): boolean => {
  return globalIsReady;
};

/**
 * 获取全局配置
 */
export const getGlobalConfig = () => {
  return globalConfig;
};

/**
 * 配置更新 Hook
 * 自动监听父页面配置更新，确保数据实时同步
 */
export const useConfig = () => {
  const [config, setConfig] = useState<GlobalConfig>(() => ({ ...globalConfig }));

  useEffect(() => {
    // 更新本地配置状态（使用函数式更新确保获取最新值）
    const updateConfig = () => {
      setConfig(prevConfig => {
        // 深度比较，只有真正变化时才更新
        const newConfig = { ...globalConfig };
        // 使用 JSON 序列化进行深度比较（对于简单对象足够）
        const prevStr = JSON.stringify(prevConfig);
        const newStr = JSON.stringify(newConfig);
        
        if (prevStr !== newStr) {
          console.log('🔄 useConfig 检测到配置变化，更新状态');
          return newConfig;
        }
        return prevConfig;
      });
    };

    // 添加配置监听器
    configListeners.add(updateConfig);

    // 初始化时设置当前配置
    updateConfig();

    // 清理函数
    return () => {
      configListeners.delete(updateConfig);
    };
  }, []);

  return config;
};

/**
 * 清理 Postmate 连接
 */
export const cleanupPostmate = () => {
  if (globalMessenger) {
    try {
      globalMessenger.emit('child:cleanup', {
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('发送清理事件失败:', error);
    }
  }

  globalMessenger = null;
  globalIsReady = false;
  globalIsInitializing = false;
  initPromise = null;
  globalConfig = {};
  globalExpenseLinks = { toolCallId: undefined, selectedData: [] };

  notifyListeners();
  notifyConfigListeners();
  notifyExpenseLinkListeners(globalExpenseLinks);
};

/**
 * 初始化 Postmate 连接（单例模式）
 */
const initializePostmate = (): Promise<Postmate.ChildAPI> => {
  if (initPromise) {
    return initPromise;
  }

  if (globalMessenger) {
    return Promise.resolve(globalMessenger);
  }

  if (globalIsInitializing) {
    // 如果正在初始化，等待完成
    return new Promise((resolve, reject) => {
      const checkStatus = () => {
        if (globalMessenger) {
          resolve(globalMessenger);
        } else if (!globalIsInitializing) {
          reject(new Error('初始化失败'));
        } else {
          setTimeout(checkStatus, 100);
        }
      };
      checkStatus();
    });
  }

  globalIsInitializing = true;

  initPromise = new Postmate.Model({
    // 暴露给父页面的方法
    updateConfig: (config: any) => {
      console.log('🔄 收到父页面配置更新:', config);

      // 判断是否为对象（非数组）
      const isObject = (item: any): boolean => {
        return item && typeof item === 'object' && !Array.isArray(item);
      };

      // 深度合并配置（处理嵌套对象和数组）
      const deepMerge = (target: any, source: any): any => {
        const output = { ...target };
        
        if (isObject(target) && isObject(source)) {
          Object.keys(source).forEach(key => {
            if (isObject(source[key]) && !Array.isArray(source[key])) {
              // 递归合并对象
              output[key] = deepMerge(target[key] || {}, source[key]);
            } else {
              // 直接覆盖数组和基本类型
              output[key] = source[key];
            }
          });
        }
        
        return output;
      };

      // 更新全局配置（深度合并）
      globalConfig = deepMerge(globalConfig, config);

      console.log('📦 全局配置已更新:', globalConfig);

      // 通知所有配置监听器
      notifyConfigListeners();

      return Promise.resolve(globalConfig);
    },

    getStatus: () => {
      return Promise.resolve({
        ready: globalIsReady,
        config: globalConfig,
        timestamp: Date.now()
      });
    },
    async onExpenseLinkSelected(payload: any) {
      const { id, selectedData, toolCallId } = payload || {};
      console.log('🔄 收到父页面关联申请选择:', id, selectedData, toolCallId);

      // 更新全局选择数据并通知监听者（带 toolCallId）
      const normalized = Array.isArray(selectedData) ? selectedData : (selectedData ? [selectedData] : []);
      globalExpenseLinks = { toolCallId, selectedData: normalized };
      notifyExpenseLinkListeners(globalExpenseLinks);

      return true;
    },

    async onFlowTemplateSelected(payload: any = {}) { 
      // 更新全局选择数据并通知监听者
      globalFlowTemplate = payload;
      notifyFlowTemplateListeners(payload);

      return true;
    },

    async onFeeTypeSelected(payload: any = {}) { 
      // 更新全局选择数据并通知监听者
      globalFeeType = payload;
      notifyFeeTypeListeners(payload);

      return true;
    },

    async onDialogConfirm(payload: any = {}) {
      // 处理对话框确认回调
      const { callbackId } = payload || {};
      if (callbackId) {
        // 动态导入以避免循环依赖
        import('../utils/postmate').then(module => {
          module.executeDialogCallback(callbackId);
        }).catch(error => {
          console.error('执行对话框回调失败:', error);
        });
      }
      return true;
    },

    triggerReimburse(payload: any[] = []) {
      const arr = Array.isArray(payload) ? payload : [];
      globalReimbursePayload = arr;
      notifyReimburseListeners(arr);
      return true;
    },

    async handleParentResponse(payload: any = {}) {
      // 处理父应用对消息的回复
      const { messageId, data, isError } = payload || {};
      console.log('🔄 收到父页面消息回复:', { messageId, data, isError });
      
      if (messageId) {
        // 动态导入以避免循环依赖
        import('../utils/postmate').then(module => {
          module.handleParentResponse(messageId, data, isError);
        }).catch(error => {
          console.error('处理父应用回复失败:', error);
        });
      }
      return true;
    }
  }).then((parent) => {
    console.log('✅ Postmate handshake 成功');
    globalMessenger = parent;
    globalIsReady = true;
    globalIsInitializing = false;

    // 通知父页面子应用已准备就绪
    parent.emit('child:ready', {
      timestamp: Date.now(),
      url: window.location.href
    });

    notifyListeners();
    return parent;
  }).catch((error) => {
    console.error('❌ Postmate handshake 失败:', error);
    globalMessenger = null;
    globalIsReady = false;
    globalIsInitializing = false;
    initPromise = null;

    notifyListeners();
    throw error;
  });

  return initPromise;
};

/**
 * Postmate Hook（单例模式）
 */
export const usePostmate = () => {
  const [messenger, setMessenger] = useState<Postmate.ChildAPI | null>(globalMessenger);
  const [isReady, setIsReady] = useState<boolean>(globalIsReady);
  const isEmbedded = useIsEmbedded();

  useEffect(() => {
    // 更新本地状态
    const updateState = () => {
      setMessenger(globalMessenger);
      setIsReady(globalIsReady);
    };

    // 添加监听器
    listeners.add(updateState);

    // 如果不在嵌入环境，直接标记为就绪
    if (!isEmbedded) {
      setIsReady(true);
      return () => {
        listeners.delete(updateState);
      };
    }

    // 如果还没有初始化，开始初始化
    if (!globalMessenger && !globalIsInitializing) {
      initializePostmate().catch((error) => {
        console.error('Postmate 初始化失败:', error);
      });
    }

    // 清理函数
    return () => {
      listeners.delete(updateState);
    };
  }, [isEmbedded]);

  return {
    messenger,
    isReady,
    isEmbedded
  };
};

/**
 * 订阅父页面通过 onExpenseLinkSelected 回传的关联申请选择结果
 */
export const useExpenseLinkSelection = () => {
  const [expenseLinks, setExpenseLinks] = useState<any>(globalExpenseLinks);
  useEffect(() => {
    const update = (template: any) => setExpenseLinks(template);
    expenseLinkListeners.add(update);
    // 初始化同步一次
    update(globalExpenseLinks);
    return () => {
      expenseLinkListeners.delete(update);
    };
  }, []);

  return expenseLinks;
};

/**
 * 订阅父页面通过 onFlowTemplateSelected 回传的单据模板选择结果
 */
export const useFlowTemplateSelection = () => {
  const [flowTemplate, setFlowTemplate] = useState<any>(globalFlowTemplate);

  useEffect(() => {
    const update = (template: any) => setFlowTemplate(template);
    flowTemplateListeners.add(update);
    // 初始化
    update(globalFlowTemplate);
    return () => {
      flowTemplateListeners.delete(update);
    };
  }, []);

  return flowTemplate;
};

/**
 * 订阅父页面通过 onFeeTypeSelected 回传的费用类型选择结果
 */
export const useFeeTypeSelection = () => {
  const [feeType, setFeeType] = useState<any>(globalFeeType);

  useEffect(() => {
    const update = (feeType: any) => setFeeType(feeType);
    feeTypeListeners.add(update);
    // 初始化
    update(globalFeeType);
    return () => {
      feeTypeListeners.delete(update);
    };
  }, []);

  return feeType;
};

/**
 * 订阅父页面触发的报销指令
 */
export const useReimburseTrigger = () => {
  const [payload, setPayload] = useState<any[]>(globalReimbursePayload);

  useEffect(() => {
    const update = (data: any[]) => setPayload(data);
    reimburseListeners.add(update);
    // 初始化同步一次
    update(globalReimbursePayload);
    return () => {
      reimburseListeners.delete(update);
    };
  }, []);

  return payload;
};

/**
 * 聊天初始化配置Hook
 * 管理sessionId、specificationId等初始化参数
 */
export const useChatInitialization = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [specificationId, setSpecificationId] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [flowCode, setFlowCode] = useState('');
  const [flowId, setFlowId] = useState('');
  const [auditFlowCharge, setAuditFlowCharge] = useState(false);
  const [page, setPage] = useState('');
  const isEmbedded = useIsEmbedded();

  useEffect(() => {
    // 按优先级获取sessionId和specificationId
    let finalSessionId = '';
    let finalSpecificationId = '';

    // 1. 优先从URL参数获取
    const urlSessionId = getParamFromUrl('sessionId');
    const urlSpecificationId = getParamFromUrl('specificationId');
    const urlFlowId = getParamFromUrl('flowId') || '';
    const urlFlowCode = getParamFromUrl('flowCode') || '';
    const urlAuditFlowCharge = getParamFromUrl('auditFlowCharge') === '1' || false;
    const urlPage = getParamFromUrl('page') || '';

    if (urlSessionId) {
      finalSessionId = urlSessionId;
      console.log('从URL参数获取sessionId:', finalSessionId);
    }

    if (urlSpecificationId) {
      finalSpecificationId = urlSpecificationId;
      console.log('从URL参数获取specificationId:', finalSpecificationId);
    }

    // 2. 如果URL没有，且在iframe环境中，等待iframe消息
    if ((!finalSessionId) && isEmbedded) {
      console.log('在iframe环境中，等待父窗口传递参数...');
      // 参数将通过useConfigUpdateListener设置
      setIsInitialized(true);
      return;
    }

    // 3. 如果都没有，使用默认值
    if (!finalSessionId) {
      finalSessionId = 'default-session';
      console.log('使用默认sessionId:', finalSessionId);
    }

    setSessionId(finalSessionId);
    setSpecificationId(finalSpecificationId);
    setIsInitialized(true);
    setFlowId(urlFlowId)
    setFlowCode(urlFlowCode)
    setAuditFlowCharge(urlAuditFlowCharge)
    setPage(urlPage)
  }, [isEmbedded]);

  // 如果参数后来通过iframe消息设置了，标记为已初始化
  useEffect(() => {
    if ((sessionId || specificationId) && !isInitialized) {
      setIsInitialized(true);
    }
  }, [sessionId, specificationId, isInitialized]);

  return {
    sessionId: sessionId || 'default-session', // 确保始终有值
    specificationId,
    isInitialized,
    isEmbedded,
    flowCode,
    flowId,
    auditFlowCharge,
    page
  };
}; 